import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";
import { API_URL } from "../config";
import { preloadTeamLogos } from "../utils/imageCache";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Define logout functions first
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
  }, []);

  const logoutWithMessage = useCallback(
    (message) => {
      logout();
      // Store the message in localStorage to be picked up by the login page
      localStorage.setItem("sessionExpiredMessage", message);
      // Redirect to login page
      window.location.href = "/login";
    },
    [logout],
  );

  useEffect(() => {
    // Set default axios auth header
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // Setup axios response interceptor to handle token expiration
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          const errorMessage =
            error.response?.data?.error?.message || error.response?.data?.error;
          if (
            errorMessage === "Invalid or expired token" ||
            errorMessage === "Token missing"
          ) {
            // Token is expired or invalid, logout and redirect
            logoutWithMessage("Your session has expired. Please log in again.");
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      },
    );

    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/users/me`);
        setUser(response.data);
      } catch (error) {
        const errorMessage =
          error.response?.data?.error?.message || error.response?.data?.error;
        if (
          errorMessage === "Invalid or expired token" ||
          errorMessage === "Token missing"
        ) {
          logoutWithMessage("Your session has expired. Please log in again.");
        } else {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    verifyToken();

    // Cleanup interceptor on unmount or token change
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [token, logoutWithMessage]); // Add logoutWithMessage to dependencies

  const login = async (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("token", authToken);
    axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    
    // Preload team logos after successful login (silently in background)
    try {
      const teamsResponse = await axios.get(`${API_URL}/teams`);
      const teams = teamsResponse.data.teams || teamsResponse.data;
      console.log('Fetched teams for preloading:', teams?.length, 'teams');
      if (teams && teams.length > 0) {
        // Preload logos silently in the background
        preloadTeamLogos(teams).catch(error => {
          console.warn('Failed to preload team logos:', error);
        });
      }
    } catch (error) {
      console.warn('Failed to fetch teams for logo preloading:', error);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    logoutWithMessage,
    updateUser,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
